export type VoiceHoldKey = 'Control' | 'Alt' | 'CapsLock'
export type VoiceContinuousSecondKey = 'Enter' | 'Space'

export interface VoiceShortcutConfig {
  pushToTalkKey: VoiceHoldKey
  continuousSecondKey: VoiceContinuousSecondKey
}

export interface VoiceShortcutLabels {
  pushToTalk: string
  continuous: string
}

export const DEFAULT_VOICE_SHORTCUTS: VoiceShortcutConfig = {
  pushToTalkKey: 'Control',
  continuousSecondKey: 'Enter',
}

export const VOICE_HOLD_KEY_OPTIONS: Array<{ value: VoiceHoldKey; label: string }> = [
  { value: 'Control', label: 'Ctrl' },
  { value: 'Alt', label: 'Alt' },
  { value: 'CapsLock', label: 'Caps Lock' },
]

export const VOICE_CONTINUOUS_SECOND_KEY_OPTIONS: Array<{ value: VoiceContinuousSecondKey; label: string }> = [
  { value: 'Enter', label: 'Enter' },
  { value: 'Space', label: 'Espacio' },
]

const HOLD_KEY_CODES: Record<VoiceHoldKey, string[]> = {
  Control: ['ControlLeft', 'ControlRight'],
  Alt: ['AltLeft', 'AltRight'],
  CapsLock: ['CapsLock'],
}

const SECOND_KEY_CODES: Record<VoiceContinuousSecondKey, string> = {
  Enter: 'Enter',
  Space: 'Space',
}

const HOLD_KEY_LABELS: Record<VoiceHoldKey, string> = {
  Control: 'Ctrl',
  Alt: 'Alt',
  CapsLock: 'Caps Lock',
}

const SECOND_KEY_LABELS: Record<VoiceContinuousSecondKey, string> = {
  Enter: 'Enter',
  Space: 'Espacio',
}

const HOLD_KEY_VALUES: Record<VoiceHoldKey, string[]> = {
  Control: ['Control'],
  Alt: ['Alt'],
  CapsLock: ['CapsLock'],
}

const SECOND_KEY_VALUES: Record<VoiceContinuousSecondKey, string[]> = {
  Enter: ['Enter'],
  Space: [' ', 'Spacebar'],
}

function isVoiceHoldKey(value: unknown): value is VoiceHoldKey {
  return value === 'Control' || value === 'Alt' || value === 'CapsLock'
}

function isVoiceContinuousSecondKey(value: unknown): value is VoiceContinuousSecondKey {
  return value === 'Enter' || value === 'Space'
}

export function normalizeVoiceShortcuts(shortcuts?: Partial<VoiceShortcutConfig>): VoiceShortcutConfig {
  return {
    pushToTalkKey: isVoiceHoldKey(shortcuts?.pushToTalkKey)
      ? shortcuts.pushToTalkKey
      : DEFAULT_VOICE_SHORTCUTS.pushToTalkKey,
    continuousSecondKey: isVoiceContinuousSecondKey(shortcuts?.continuousSecondKey)
      ? shortcuts.continuousSecondKey
      : DEFAULT_VOICE_SHORTCUTS.continuousSecondKey,
  }
}

export function isPushToTalkKey(
  event: Pick<KeyboardEvent, 'code'> & Partial<Pick<KeyboardEvent, 'key'>>,
  shortcuts?: Partial<VoiceShortcutConfig>
): boolean {
  const normalized = normalizeVoiceShortcuts(shortcuts)
  return HOLD_KEY_CODES[normalized.pushToTalkKey].includes(event.code)
    || HOLD_KEY_VALUES[normalized.pushToTalkKey].includes(event.key ?? '')
}

export function isContinuousSecondKey(
  event: Pick<KeyboardEvent, 'code'> & Partial<Pick<KeyboardEvent, 'key'>>,
  shortcuts?: Partial<VoiceShortcutConfig>
): boolean {
  const normalized = normalizeVoiceShortcuts(shortcuts)
  return event.code === SECOND_KEY_CODES[normalized.continuousSecondKey]
    || SECOND_KEY_VALUES[normalized.continuousSecondKey].includes(event.key ?? '')
}

export function isPushToTalkModifierActive(
  event: Partial<Pick<KeyboardEvent, 'ctrlKey' | 'altKey'>> & Pick<KeyboardEvent, 'code'> & Partial<Pick<KeyboardEvent, 'key'>>,
  shortcuts?: Partial<VoiceShortcutConfig>
): boolean {
  const normalized = normalizeVoiceShortcuts(shortcuts)
  if (isPushToTalkKey(event, normalized)) return true
  if (normalized.pushToTalkKey === 'Control') return Boolean(event.ctrlKey)
  if (normalized.pushToTalkKey === 'Alt') return Boolean(event.altKey)
  return false
}

export function getVoiceShortcutLabels(
  shortcuts?: Partial<VoiceShortcutConfig>,
  platform = typeof navigator === 'undefined' ? '' : navigator.platform,
): VoiceShortcutLabels {
  const normalized = normalizeVoiceShortcuts(shortcuts)
  const pushToTalk = /Mac/i.test(platform) && normalized.pushToTalkKey === 'Control'
    ? 'Control ⌃'
    : HOLD_KEY_LABELS[normalized.pushToTalkKey]
  return {
    pushToTalk,
    continuous: `${pushToTalk}+${SECOND_KEY_LABELS[normalized.continuousSecondKey]}`,
  }
}
